import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import { isEmpty, groupBy } from 'lodash';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import SearchIcon from '@material-ui/icons/Search';
import { useParams } from 'react-router-dom';

import { useFuse } from 'hooks';

import { getStaff, getStaffAvailability } from 'api/staff';
import {
  Component,
  Overview,
  OverviewDetails,
  DetailsTitle,
  Details,
  SearchWrapper,
  SearchBar,
  Search,
} from './styles';
import StaffListSection from './StaffListSection';

const Staffing = memo(
  ({
    clinicStaff,
    schedule,
    sublocation,
    selectedStaff,
    staffList,
    setStaffList,
    setSelectedStaff,
    setBookedStaff,
    clinicHours,
    recommendedNurses,
    startTime,
    endTime,
    endDate,
    startDate,
  }) => {
    if (!clinicStaff) {
      return null;
    }

    const { id: clinicId } = useParams();

    const recommendedNursingHour = recommendedNurses * clinicHours;

    const { hits, onSearch, query } = useFuse(staffList, {
      includeScore: true,
      includeMatches: true,
      shouldSort: true,
      keys: [
        'first_name',
        'last_name',
        'type',
        'email',
        'phone',
        'address',
        'city',
        'state',
        'postal_code',
        'country',
        'rate',
        'mileage_rate',
      ],
    });
    const [changes, setChanges] = useState(false);

    const onChange = (e) => {
      onSearch(e);
    };

    const nursesNeededLabel = recommendedNurses > 1 ? 'nurses' : 'nurse';
    const nursingHoursLabel =
      recommendedNurses * clinicHours > 1 ? 'hrs' : 'hr';

    const selectedStaffIds = selectedStaff.map((id) => id);

    const staffToFormat =
      query === '' ? staffList : hits.map(({ item }) => item);

    const formattedHits = staffToFormat.map((hit) => ({
      ...hit,
      ...(changes
        ? {}
        : {
            selected: selectedStaffIds.includes(hit.id)
              ? 'selected'
              : 'unselected',
          }),
    }));

    const groupedStaff = groupBy(formattedHits, 'selected');
    const groupedSelectedStaffByType = groupBy(groupedStaff.selected, 'type');

    const fetchStaffList = async () => {
      const unselectedStaffIds = staffList
        .filter(({ selected }) => selected === 'unselected')
        .map(({ id }) => id);

      const { data } = await getStaff(0, 10000);

      const dataWithAvailability = data.data
        .filter(({ id }) => !unselectedStaffIds.includes(id))
        .map((staff) => ({
          ...staff,
          selected: 'unselected',
        }));

      setStaffList((prevState) =>
        prevState.concat(
          dataWithAvailability.filter(
            ({ id: staffId }) => !selectedStaffIds.includes(staffId),
          ),
        ),
      );
    };

    return (
      <Component>
        {!isEmpty(schedule) && (
          <Overview>
            <OverviewDetails>
              <DetailsTitle>Recommended</DetailsTitle>
              <Details>
                {recommendedNurses} {nursesNeededLabel} -{' '}
                {recommendedNursingHour.toFixed(2)} {nursingHoursLabel}
              </Details>
            </OverviewDetails>

            <OverviewDetails>
              <DetailsTitle>Selected</DetailsTitle>
              {Object.keys(groupedSelectedStaffByType).map((type) => {
                const list = groupedSelectedStaffByType[type];
                const staffCount = list.length;
                const staffHours = staffCount * clinicHours;

                return (
                  <Details>
                    {staffCount} {type} - {staffHours} hrs
                  </Details>
                );
              })}
            </OverviewDetails>
          </Overview>
        )}

        <SearchWrapper>
          <SearchBar>
            <SearchIcon />
            <Search
              name="search"
              type="search"
              placeholder="Search staff"
              onChange={onChange}
              onKeyUp={onSearch}
              autoComplete="off"
            />
          </SearchBar>
        </SearchWrapper>

        <StaffListSection
          title="BOOKED"
          staffList={groupedStaff.selected}
          startDate={startDate}
          endDate={endDate}
          startTime={startTime}
          endTime={endTime}
          clinicStaff={clinicStaff}
          sublocation={sublocation}
          setSelectedStaff={setSelectedStaff}
          setChanges={setChanges}
          setStaffList={setStaffList}
        />

        <StaffListSection
          title="STAFF LIST"
          staffList={groupedStaff.unselected || []}
          startDate={startDate}
          endDate={endDate}
          startTime={startTime}
          endTime={endTime}
          clinicStaff={clinicStaff}
          sublocation={sublocation}
          fetchStaffList={fetchStaffList}
          setStaffList={setStaffList}
          setSelectedStaff={setSelectedStaff}
          setChanges={setChanges}
          isStaffList
        />
      </Component>
    );
  },
);

export default Staffing;
